import Box from '@mui/material/Box';

import { useCountUp } from '@/hooks/useCountUp';

import { VisuallyHidden } from './VisuallyHidden';

type Props = {
  value: number;
  /** Rendered after the number, e.g. "+" or "%". */
  suffix?: string;
  /** Rendered before the number, e.g. "USD ". */
  prefix?: string;
  /**
   * Whether the figure takes digit grouping. On by default, because a
   * quantity reads better as `1,200` than as `1200`.
   *
   * A year is the exception and the reason this exists: it is a number you
   * can count to but must never punctuate, and `toLocaleString` has no idea
   * which it is being handed, so it rendered an established date of 2018 as
   * "2,018" wherever a company fact used this.
   */
  grouped?: boolean;
};

/**
 * A figure that counts up once, when it scrolls into view.
 *
 * The count itself is hidden from assistive technology and the finished value
 * is exposed as real text beside it, so a screen reader announces "500+" once
 * instead of narrating every frame. `aria-label` on a bare span is not enough:
 * without a role, several screen readers ignore it.
 */
export function CountUp({ value, suffix = '', prefix = '', grouped = true }: Props) {
  const { ref, shown } = useCountUp(value);
  const format = (n: number) => (grouped ? n.toLocaleString('en-UG') : String(n));
  const final = `${prefix}${format(value)}${suffix}`;

  return (
    <Box component="span" ref={ref}>
      <Box component="span" aria-hidden>
        {prefix}
        {format(shown)}
        {suffix}
      </Box>
      <VisuallyHidden>{final}</VisuallyHidden>
    </Box>
  );
}
